import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory, NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import { Skeleton, Divider, List, Collapse, Alert, ListItem, ListItemText } from '@mui/material';
import { getVersion as getBookingsVersion } from '@eatclub-apps/ec-bookings-library';
import { Box } from '@eatclubcode/ec-component-library';
import { useTheme } from '@mui/material/styles';
import { restaurantActivePropTypes } from '../../../data/models/Restaurant';
import { overrideDisabledRestaurants } from '../../../utils/flags';
import { findObjectByProperty, getVersionText } from '../../../utils/helpers';
import { hasAccess } from '../../../utils/permissions';
import useStyles from './NavigationDrawerStyles';
import DownArrowSVG from '../../../assets/down_caret.svg';
import HideNavigationSVG from '../../../assets/hide_navigation.svg';
import RestaurantSwitcher from './RestaurantSwitcher/RestaurantSwitcher';
import { COLORS } from '../../../EatClubTheme';
import { userPropTypes } from '../../../data/models/User';
import Mixpanel from '../../../lib/mixpanel';

const NavigationDrawer = ({
  hideDrawer,
  navigationItems,
  allowHideNavigation,
  user,
  restaurantFetching,
  restaurantActive,
}) => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();

  const [collapsibles, setCollapsibles] = useState([]);

  // Check whether a nav item is selected (we're on that page)
  const isSelected = (slug) => {
    return slug !== null && (location?.pathname === slug || location?.pathname.startsWith(slug));
  };

  // Set up the navigation collapsibles
  useEffect(() => {
    const navsWithSubItems = navigationItems.filter((nav) => nav?.subItems?.length > 0);
    const navigationCollapsibles = navsWithSubItems?.map((nav) => {
      // Also, if we're on the selected screen, open the collapsible
      const hasSelectedNav =
        nav?.subItems?.filter((subItem) => {
          return isSelected(`/${subItem?.slug?.toLowerCase().replace(/ /g, '-')}`);
        }).length > 0;

      return {
        id: nav?.id,
        name: nav?.name,
        value: hasSelectedNav, // Whether it is open
      };
    });

    setCollapsibles(navigationCollapsibles);
  }, [navigationItems]);

  /**
   * Toggle a collapsible nav item on or off
   */
  const toggleCollapsible = (id) => {
    const newCollapsibles = collapsibles?.map((collapsible) => {
      if (collapsible?.id === id) {
        return { ...collapsible, value: !collapsible?.value };
      }

      return collapsible;
    });

    setCollapsibles(newCollapsibles);
  };

  const getNavLink = (slug) => `/${slug.toLowerCase().replace(/ /g, '-')}`;

  const loadingItem = (key) => (
    <Skeleton height={42} width={260} style={{ margin: 'auto', borderRadius: '12px' }} key={key} />
  );

  const isStaff = user.userInfo?.userType === 'staff';
  const isLoading = (restaurantFetching || user?.userFetching) && !isStaff;

  const getNavListItem = (nav, navLink, selected) => (
    <ListItem
      button
      key={nav.id}
      component={NavLink}
      to={navLink}
      onClick={() => {
        Mixpanel.track(`Nav link clicked - ${nav.id}`, {
          page: nav.id,
          origin: window.location.origin,
        });
        hideDrawer();
      }}
      selected={selected}
      className={classes.navigationLink}
      style={{
        color: selected ? '#000000' : theme.colors.smoke,
        ...(selected ? { backgroundColor: theme.colors.menuButtonHoverBackground } : {}),
      }}
    >
      <ListItemText
        className={classes.navText}
        disableTypography
        primary={
          <Box>
            <Box className={classes.navItemLabel}>
              {nav.name} {nav?.isNew && <Box className={classes.newLabel}>New</Box>}
            </Box>
          </Box>
        }
        style={{
          color: selected ? '#000000' : theme.colors.smoke,
          ...(selected && { backgroundColor: theme.colors.menuButtonHoverBackground }),
        }}
      />
    </ListItem>
  );

  return (
    <Box className={classes.sidebar}>
      <Box className={classes.sidebarNavigation}>
        <Box>
          <RestaurantSwitcher hideDrawer={hideDrawer} />
          {isStaff && (
            <>
              <Box style={{ marginTop: '8px' }}>
                <Alert
                  severity='info'
                  style={{ borderRadius: '20px', backgroundColor: '#E3EBF6' }}
                >{`Logged in as ${user.userInfo.name}`}</Alert>
              </Box>

              {restaurantActive?.restaurant?.objectId &&
                !restaurantActive?.restaurant?.enabled &&
                !overrideDisabledRestaurants.includes(restaurantActive?.restaurant?.objectId) && (
                  <Box style={{ marginTop: '8px' }}>
                    <Alert
                      severity='info'
                      style={{ borderRadius: '20px', backgroundColor: '#E3EBF6' }}
                    >
                      This restaurant is temporarily disabled
                    </Alert>
                  </Box>
                )}

              <Divider className={classes.divider} />
              {/* <Divider className={classes.divider} style={{ margin: '10px 20px', backgroundColor: COLORS.SHARK }} /> */}
            </>
          )}
          <List className={classes.list}>
            {navigationItems.map((nav) => {
              if (nav.section !== 1) {
                return <React.Fragment key={nav?.id} />;
              }

              const noPermission = nav?.allowedScopes && !hasAccess(user, nav?.allowedScopes);
              if (noPermission) {
                return null;
              }

              if (nav?.subItems?.length > 0) {
                if (isLoading) {
                  return loadingItem(nav?.id);
                }

                const open =
                  nav?.alwaysOpen ||
                  findObjectByProperty(collapsibles, nav?.id, 'id')?.value === true;

                const dropdownOnly = !nav?.slug;
                const navLink = !dropdownOnly ? getNavLink(nav.slug) : null;
                const selected = !dropdownOnly
                  ? isSelected(navLink)
                  : nav.subItems.some((subItem) => {
                      const subNavLink = getNavLink(subItem?.slug);
                      return isSelected(subNavLink);
                    });

                return (
                  <Box key={nav?.id}>
                    {!dropdownOnly ? (
                      getNavListItem(nav, navLink, selected)
                    ) : (
                      <Box
                        onClick={() => {
                          toggleCollapsible(nav?.id);
                        }}
                        /* to='#' */
                        className={classes.navigationLink}
                        style={{
                          color: selected ? '#000000' : '#6E6E6D',
                        }}
                      >
                        {nav.name}

                        <DownArrowSVG
                          className={classes.expandArrow}
                          style={{
                            transform: open ? 'rotate(180deg)' : '',
                          }}
                        />
                      </Box>
                    )}

                    <Collapse
                      in={open}
                      timeout='auto'
                      style={{ transitionDuration: '0.5s' }}
                      unmountOnExit
                    >
                      <List component='div' disablePadding>
                        {nav.subItems?.map((subItem) => {
                          const subNavLink = getNavLink(subItem?.slug);
                          const selected = isSelected(subNavLink);

                          return (
                            <Box
                              key={`sub-${subItem.id}`}
                              onClick={() => {
                                Mixpanel.track(`Nav link clicked - ${subItem.id}`, {
                                  page: subItem.id,
                                  origin: window.location.origin,
                                });
                                hideDrawer();
                                history.push(subNavLink);
                              }}
                              className={classes.navigationLink}
                              style={{
                                backgroundColor: selected
                                  ? theme.colors.menuButtonHoverBackground
                                  : '',
                                color: selected ? '#000000' : theme.colors.smoke,
                                paddingLeft: '24px',
                              }}
                            >
                              {subItem.name}{' '}
                              {subItem?.isNew && <Box className={classes.newLabel}>New</Box>}
                            </Box>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Box>
                );
              }

              if (isLoading) {
                return loadingItem(nav?.id);
              }

              const navLink = getNavLink(nav?.slug);
              const selected = isSelected(navLink);

              return getNavListItem(nav, navLink, selected);
            })}
          </List>

          <Divider className={classes.divider} />

          <List className={classes.list}>
            {navigationItems.map((nav) => {
              if (nav.section !== 2) {
                return <React.Fragment key={nav?.id} />;
              }

              if (isLoading) {
                return loadingItem(nav?.id);
              }

              const navLink = getNavLink(nav?.slug);
              const selected = isSelected(navLink);

              return (
                <ListItem
                  button
                  key={nav.id}
                  component={NavLink}
                  to={navLink}
                  onClick={() => {
                    Mixpanel.track(`Nav link clicked - ${nav.id}`, {
                      page: nav.id,
                      origin: window.location.origin,
                    });
                    hideDrawer();
                  }}
                  selected={selected}
                  className={classes.navigationLink}
                  style={{
                    color: selected ? '#000000' : theme.colors.smoke,
                    ...(selected
                      ? { backgroundColor: theme.colors.menuButtonHoverBackground }
                      : {}),
                  }}
                >
                  <ListItemText
                    className={classes.navText}
                    disableTypography
                    primary={nav.name}
                    style={{
                      color: selected ? '#000000' : theme.colors.smoke,
                      ...(selected && { backgroundColor: theme.colors.menuButtonHoverBackground }),
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
        <Box>
          <Box className={classes.versionNumber}>
            {getVersionText()}, {getBookingsVersion()}
          </Box>

          {allowHideNavigation && (
            <>
              <Divider className={classes.divider} />

              <Box style={{ marginTop: '8px' }}>
                <Box className={classes.navigationLink} onClick={hideDrawer}>
                  <HideNavigationSVG style={{ color: COLORS.BLACK }} />
                  <Box
                    style={{
                      textTransform: 'uppercase',
                      letterSpacing: 1.5,
                      lineHeight: '30px',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    Hide navigation
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

NavigationDrawer.propTypes = {
  hideDrawer: PropTypes.func.isRequired,
  allowHideNavigation: PropTypes.bool.isRequired,
  restaurantFetching: PropTypes.bool.isRequired,
  navigationItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: userPropTypes.isRequired,
  restaurantActive: restaurantActivePropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
  restaurantFetching: state.restaurants.fetching,
  restaurantActive: state.restaurantActive,
});

export default connect(mapStateToProps, null)(NavigationDrawer);
